<template>
  <b-form novalidate @submit.prevent="submit" class="ml-auto mr-auto">
    <b-row>
      <b-col v-if="showError" class="col-12 col-md-6 mb-3">
        <b-card>
          <p v-if="showError === 'save'" class="text-center">{{ $t('rating.form.error') }}</p>
          <p v-if="showError === 'not found'" class="text-center">{{ $t('rating.form.not_found') }}</p>
        </b-card>
      </b-col>
    </b-row>
    <template v-if="showError !== 'not found'">
      <b-form-group label-for="date">
        <datepicker format="dd.MM.yyyy" :language="de" :placeholder="$t('rating.form.date.label')" :input-class="{ 'with-placeholder': !form.date, 'bg-light': true }" id="date" v-model="form.date" :bootstrap-styling="true" required :state="$v.form.date.$dirty ? !$v.form.date.$error : null" @blur="$v.form.date.$touch"></datepicker>
      </b-form-group>
      <b-card class="mb-3">
        <b-container>
          <b-row>
            <b-col class="flex-grow-0">
              <rating-labels></rating-labels>
            </b-col>
            <b-col>
              <b-row v-for="value in Object.keys($t('rating.items'))" :key="value" class="p-2">
                <b-col class="col-12">
                  <rating-select v-model="form[value]"></rating-select>
                </b-col>
              </b-row>
            </b-col>
          </b-row>
        </b-container>
      </b-card>
      <b-row v-if="loading">
        <b-col class="col-12 mb-3">
          <div class="d-flex justify-content-around flex-row mb-3"><b-spinner></b-spinner></div>
        </b-col>
      </b-row>
      <b-row>
        <b-col class="col-12 col-md-6 mb-3">
          <b-link class="bg-transparent h-100 text-primary border-primary border form-control text-center" :title="$t('rating.form.cancel')" :to="'/colony/' + colony + '/' + apiary">
            {{ $t('rating.form.cancel') }}
          </b-link>
        </b-col>
        <b-col class="col-12 col-md-6 mb-3">
          <b-button class="d-block w-100 h-100" type="submit" variant="primary">{{ $t('rating.form.submit') }}</b-button>
        </b-col>
      </b-row>
      <b-modal :id="'offLineModalRatingSave'" centered hide-header-close @ok="hideModal" class="network-modal">
        <template slot="modal-title">
          {{ $t('offLineModal.title') }}
        </template>
        {{ $t('offLineModal.text') }}
        <template slot="modal-footer" class="offline-modal-footer" slot-scope="{ ok }">
          <b-button size="lg" class="mt-3" @click="ok()">
            {{ $t('offLineModal.ok') }}
          </b-button>
        </template>
      </b-modal>
    </template>
  </b-form>
</template>

<script>
import { required } from 'vuelidate/lib/validators'
import moment from 'moment'
import RatingSelect from './RatingSelect'
import RatingLabels from './RatingLabels'
import Datepicker from 'vuejs-datepicker'
import { de } from 'vuejs-datepicker/dist/locale'

export default {
  name: 'RatingForm',
  components: { RatingLabels, RatingSelect, Datepicker },
  props: ['id', 'colony', 'apiary'],
  data () {
    const form = {
      date: new Date(),
      field_status_volksstaerke: null,
      field_status_raumreserve: null,
      field_status_weiselrichtigkeit: null,
      field_status_sanftmut: null,
      field_status_schwarmstimmung: null,
      field_status_futterversorgung: null,
      field_status_gesundheit: null,
      field_status_varroabeurteilung: null
    }

    return {
      de: de,
      showError: false,
      loading: false,
      form
    }
  },
  methods: {
    showOffLineModal () {
      this.$bvModal.show('offLineModalRatingSave')
    },
    hideModal () {
      this.$bvModal.hide('offLineModalRatingSave')
    },
    async submit () {
      if (navigator.onLine) {
        this.$v.form.$touch()
        if (!this.$v.$error) {
          this.loading = true
          const result = await this.saveRating()
          this.loading = false
          this.showError = (!result ? 'save' : false)

          if (result) {
            this.$router.replace('/colony/' + this.colony + '/' + this.apiary)
          }

          return result
        } else {
          return false
        }
      } else {
        this.showOffLineModal()
      }
    },
    async saveRating () {
      try {
        this.loading = true
        const token = await this.$api.user.getToken()
        const result = await this.$api.ratings.save({
          id: this.id,
          colony: this.colony,
          date: moment(this.form.date).format('YYYY[-]MM[-]DD'),
          field_status_volksstaerke: this.form.field_status_volksstaerke,
          field_status_raumreserve: this.form.field_status_raumreserve,
          field_status_weiselrichtigkeit: this.form.field_status_weiselrichtigkeit,
          field_status_sanftmut: this.form.field_status_sanftmut,
          field_status_schwarmstimmung: this.form.field_status_schwarmstimmung,
          field_status_futterversorgung: this.form.field_status_futterversorgung,
          field_status_gesundheit: this.form.field_status_gesundheit,
          field_status_varroabeurteilung: this.form.field_status_varroabeurteilung,
          token
        })
        this.loading = false
        return result
      } catch (e) { // network error etc.
        if (e.response && e.response.status === 403) {
          this.$router.replace('/login')
        }

        return false
      }
    },
    async loadData () {
      if (this.id) {
        try {
          const data = await this.$api.ratings.getOne(this.id)
          this.form.date = (data.date && Date.parse(data.date)) || this.form.date
          this.form.field_status_volksstaerke = data.field_status_volksstaerke || 0
          this.form.field_status_raumreserve = data.field_status_raumreserve || 0
          this.form.field_status_weiselrichtigkeit = data.field_status_weiselrichtigkeit || 0
          this.form.field_status_sanftmut = data.field_status_sanftmut || 0
          this.form.field_status_schwarmstimmung = data.field_status_schwarmstimmung || 0
          this.form.field_status_futterversorgung = data.field_status_futterversorgung || 0
          this.form.field_status_gesundheit = data.field_status_gesundheit || 0
          this.form.field_status_varroabeurteilung = data.field_status_varroabeurteilung || 0

          return data
        } catch (e) {
          this.showError = 'not found'
        }
      }

      return null
    }
  },
  validations: {
    form: {
      date: {
        required
      }
    }
  },
  mounted () {
    this.loadData()
  }
}
</script>

<style scoped>
  form {
    max-width: 350px;
    font-family: "Roboto Condensed", sans-serif;
  }
</style>
